import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import type {
  INavigationDrawerContentDestinationListProps,
  IOmit,
} from '@sixui/ui';
import { Fragment } from 'react';
import {
  faChevronDown,
  faChevronRight,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createSequence } from '@olivierpascal/helpers';
import {
  Badge,
  Expandable,
  NavigationDrawerContentDestinationList,
  Stack,
} from '@sixui/ui';
import { colorSchemeTokens } from '@sixui/ui/themes/base/colorScheme.stylex';
import stylex from '@stylexjs/stylex';
import { matchPath } from 'react-router-dom';

import type { INavigationDrawerDestinationProps } from '~/components/atoms/NavigationDrawerDestination';
import type { IApiGetUserResponseBody } from '~/lib/api/modules/Users';
import { NavigationDrawerDestination } from '~/components/atoms/NavigationDrawerDestination';
import { useUserQuery } from '~/lib/api/modules/Users/hooks/useUserQuery';
import { SidebarSectionItemSkeleton } from './SidebarSectionItemSkeleton';

const DEFAULT_SKELETON_COUNT = 8;

export type ISidebarSectionProps =
  INavigationDrawerContentDestinationListProps & {
    label?: React.ReactNode;
    items?: Array<ISidebarSectionItem>;
    loading?: boolean;
    header?: React.ReactNode;
    footer?: React.ReactNode;
  };

export type ISidebarSectionItem = IOmit<
  INavigationDrawerDestinationProps,
  'children' | 'leadingIcon' | 'trailingIcon'
> & {
  label: React.ReactNode;
  faLeadingIcon: IconDefinition;
  faTrailingIcon?: IconDefinition;
  children?: Array<ISidebarSectionItem>;
  beta?: boolean;
  hidden?: boolean;
  restrictions?: {
    proFeature?: boolean;
    superAdmin?: boolean;
  };
};

const styles = stylex.create({
  subMenu: {
    marginLeft: 'calc(0.5rem + 16px)',
    marginTop: '0.25rem',
    paddingLeft: '0.5rem',
    borderLeftWidth: 1,
    borderLeftStyle: 'solid',
    borderLeftColor: colorSchemeTokens.outlineVariant,
  },
});

const renderItem = (
  item: ISidebarSectionItem,
  user?: IApiGetUserResponseBody,
): JSX.Element | null => {
  const { label, faLeadingIcon, faTrailingIcon, restrictions, beta, ...other } =
    item;

  const hidden = restrictions?.superAdmin && !user?.superAdmin;
  if (hidden) {
    return null;
  }

  return (
    <NavigationDrawerDestination
      leadingIcon={<FontAwesomeIcon icon={faLeadingIcon} fixedWidth />}
      trailingIcon={
        faTrailingIcon ? (
          <FontAwesomeIcon icon={faTrailingIcon} fixedWidth />
        ) : null
      }
      trailing={restrictions?.proFeature ? <Badge value="Pro" /> : undefined}
      disabled={restrictions?.proFeature && !user?.superAdmin}
      {...other}
    >
      {label} {beta ? <Badge value="Beta" /> : null}
    </NavigationDrawerDestination>
  );
};

const buildPattern = (pattern: string): string => `/:appRegion${pattern}`;

const renderItems = (
  items: Array<ISidebarSectionItem>,
  user?: IApiGetUserResponseBody,
): React.ReactNode =>
  items
    .filter((items) => !items.hidden)
    .map((item, itemIndex) => {
      const match = item.matchPatterns
        ? item.matchPatterns.some((pattern) =>
            matchPath(buildPattern(pattern), location.pathname),
          )
        : item.href
          ? matchPath(buildPattern(item.href), location.pathname)
          : false;
      const active = !!match;

      if (item.children) {
        return (
          <Expandable
            key={itemIndex}
            initiallyExpanded={active}
            trigger={({ expand, expanded }) =>
              renderItem(
                {
                  ...item,
                  faTrailingIcon: expanded ? faChevronDown : faChevronRight,
                  active: !open && active,
                  onClick: () => expand(!expanded),
                },
                user,
              )
            }
          >
            <Stack sx={styles.subMenu}>{renderItems(item.children)}</Stack>
          </Expandable>
        );
      }

      return (
        <Fragment key={itemIndex}>
          {renderItem(
            {
              ...item,
              active,
            },
            user,
          )}
        </Fragment>
      );
    });

export const SidebarSection: React.FC<ISidebarSectionProps> = (props) => {
  const {
    label,
    items,
    loading: loadingProp,
    header,
    footer,
    children,
    ...other
  } = props;
  const userQuery = useUserQuery({});
  const loading = loadingProp || userQuery.isLoading;
  const user = userQuery.data;

  if (!items && !header && !footer && !children && !loading) {
    return null;
  }

  return (
    <NavigationDrawerContentDestinationList headline={label} {...other}>
      {header}

      {loading
        ? createSequence(DEFAULT_SKELETON_COUNT).map((index) => (
            <SidebarSectionItemSkeleton key={index} />
          ))
        : items
          ? renderItems(items, user)
          : null}

      {children}

      {footer}
    </NavigationDrawerContentDestinationList>
  );
};
